import React from 'react';
import { withErrorBoundary } from 'react-error-boundary';

import NotFound from '@components/NotFound';
import PageLoader from '@components/PageLoader';

import { useFeatureFlags } from '@hooks/useFeatureFlags';
import { useSession } from '@hooks/useSession';

import type { Feature } from '../../../types/features.enum';

interface FeatureGuardProps {
  /** The feature that is being guarded. */
  feature: keyof typeof Feature;
  children?: React.ReactElement;
}

const FeatureGuard: React.FC<FeatureGuardProps> = ({
  children = null,
  feature,
}) => {
  const {
    state: { isLoadingAuth, isAuthenticated },
  } = useSession();
  const { hasFeature } = useFeatureFlags();

  const loading = isLoadingAuth || !isAuthenticated;

  React.useEffect(() => {
    if (!loading && !hasFeature(feature)) {
      throw new Error(`Feature ${feature} is not enabled`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [feature, loading]);

  if (loading) {
    return <PageLoader />;
  }

  return children;
};

export default withErrorBoundary(FeatureGuard, { FallbackComponent: NotFound });
